// Opt-in ATW route publishing. No changes to the site's records, blobs, or gateway slug.
// This is a writer, not a conforming ATW client: getRecord is authenticated PDS readback,
// not an MST proof, and record-chain readiness is not a pre-switch blob retrieval test.
import {
  assertRouteCollection,
  assertRouteName,
  assertRouteSuccessor,
  COLLECTION,
  parseAtUri,
  readCidField,
  readLink,
  readStrongRef,
  recomputeRootCid,
  validateRoutePolicy,
  validateRouteRecord,
} from "@szzt/records";
import type { MaslRecordValue, RoutePolicy, RouteRecord } from "@szzt/records";
import { recordCid } from "./records.ts";
import { SwapFailedError } from "./types.ts";
import type { FetchedRecord, PdsClient } from "./types.ts";

export type RouteIntent =
  | { kind: "site"; siteRkey: string; policy?: RoutePolicy | null }
  | { kind: "alias"; alias: string; policy?: RoutePolicy | null }
  | { kind: "retire"; successor?: string };

export interface WriteRouteInput {
  client: PdsClient;
  collection: string;
  name: string;
  intent: RouteIntent;
  /** Omitted/null means CREATE ONLY. Updates require the exact CID the caller inspected. */
  expectedCid?: string | null;
  maxRetries?: number;
  now?: () => Date;
}

export interface WriteRouteResult {
  routeUri: string;
  routeCid: string;
  collection: string;
  name: string;
  record: RouteRecord;
  unchanged: boolean;
  /** Moving/reusing a name inherits that name's existing browser storage and permissions. */
  warning?: string;
}

export class RouteConflictError extends Error {
  constructor(readonly routeUri: string, readonly expectedCid: string | null, readonly actualCid: string | null) {
    super(`route conflict at ${routeUri}: expected ${expectedCid ?? "absence"}, found ${actualCid ?? "absence"}; inspect and explicitly authorize the new CID before editing`);
    this.name = "RouteConflictError";
  }
}

export class RouteTargetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RouteTargetError";
  }
}

async function getOwnRecord(client: PdsClient, uri: string, expectedCollection: string): Promise<FetchedRecord> {
  const p = parseAtUri(uri);
  if (p.did !== client.did || p.collection !== expectedCollection) {
    throw new RouteTargetError(`target chain must stay in ${client.did}/${expectedCollection}: ${uri}`);
  }
  assertRouteName(p.rkey);
  const record = await client.getRecord(p.collection, p.rkey);
  if (record === null) throw new RouteTargetError(`target record is missing: ${uri}`);
  if (record.uri !== uri || (record.value as { $type?: unknown } | null)?.$type !== expectedCollection) {
    throw new RouteTargetError(`target record has the wrong URI or $type: ${uri}`);
  }
  return record;
}

/** Check the installed site's record chain and both strong refs using PDS-returned wire CIDs.
 * Never re-encode a fetched record to replace its wire CID (the encoders have distinct domains). */
async function checkSite(client: PdsClient, siteRkey: string): Promise<string> {
  const uri = `at://${client.did}/${COLLECTION.site}/${siteRkey}`;
  const site = await getOwnRecord(client, uri, COLLECTION.site);
  const current = readStrongRef(site.value, "current", COLLECTION.site);
  const deployment = await getOwnRecord(client, current.uri, COLLECTION.deployment);
  if (deployment.cid !== current.cid) throw new RouteTargetError("site.current CID does not match its deployment");
  const tile = readStrongRef(deployment.value, "tile", COLLECTION.deployment);
  const manifest = await getOwnRecord(client, tile.uri, COLLECTION.masl);
  if (manifest.cid !== tile.cid) throw new RouteTargetError("deployment.tile CID does not match its manifest record");
  const root = readLink(deployment.value, "root", COLLECTION.deployment);
  const declared = readCidField(manifest.value, "cid", COLLECTION.masl);
  const computed = await recomputeRootCid((manifest.value as MaslRecordValue).tile);
  if (root !== declared || root !== computed) throw new RouteTargetError("deployment root and manifest content disagree");
  return uri;
}

/** Validate all local arguments before login/network when called by the CLI as well as the writer. */
export function validateRouteWrite(input: Pick<WriteRouteInput, "collection" | "name" | "intent" | "expectedCid">): void {
  assertRouteCollection(input.collection);
  assertRouteName(input.name);
  if (input.expectedCid !== undefined && input.expectedCid !== null && !/^[^\s]+$/.test(input.expectedCid)) {
    throw new RouteTargetError("expected CID must be a non-empty string without whitespace");
  }
  const intent = input.intent;
  switch (intent.kind) {
    case "site": assertRouteName(intent.siteRkey); break;
    case "alias":
      assertRouteName(intent.alias);
      if (intent.alias === input.name) throw new RouteTargetError("a route cannot alias itself");
      break;
    case "retire":
      if (intent.successor !== undefined) assertRouteSuccessor(intent.successor);
      return;
    default: throw new RouteTargetError("unknown route operation");
  }
  if (intent.policy !== undefined && intent.policy !== null) validateRoutePolicy(intent.policy);
}

/** Write one route with BOTH record CAS and repo CAS. A retry re-reads every dependency under a
 * fresh head but never changes the caller's expected route CID. Concurrent route edits therefore
 * fail rather than being silently adopted and overwritten. Unrelated repo writes can retry safely. */
export async function writeRoute(input: WriteRouteInput): Promise<WriteRouteResult> {
  validateRouteWrite(input);
  const { client, collection, name, intent, maxRetries = 3 } = input;
  if (!Number.isInteger(maxRetries) || maxRetries < 0 || maxRetries > 10) throw new RouteTargetError("maxRetries must be an integer from 0 to 10");
  const expectedCid = input.expectedCid ?? null;
  const routeUri = `at://${client.did}/${collection}/${name}`;
  const createdAt = (input.now ?? (() => new Date()))().toISOString();

  for (let attempt = 0; ; attempt++) {
    // Capture the head BEFORE checking the site chain / alias. A prune, site update, or alias
    // conversion racing these reads invalidates swapCommit instead of installing a dangling route.
    const head = await client.getLatestCommit();
    const existing = await client.getRecord(collection, name);
    if ((existing?.cid ?? null) !== expectedCid) {
      throw new RouteConflictError(routeUri, expectedCid, existing?.cid ?? null);
    }
    const prior = existing === null ? undefined : validateRouteRecord(existing.value, collection);

    // Preserve unknown metadata, but never accidentally carry the old variant's control fields.
    const { targets: _targets, alias: _alias, status: _status, successor: _successor, policy: _policy, ...metadata } = prior ?? {};
    const next: Record<string, unknown> = { ...metadata, $type: collection };
    // Existing routes may omit this optional timestamp. Don't fabricate a creation time on edit.
    if (existing === null) next.createdAt = createdAt;
    if (intent.kind === "retire") {
      if (existing === null) throw new RouteTargetError("cannot retire a route that does not exist");
      next.status = "retired";
      if (intent.successor !== undefined) next.successor = intent.successor;
    } else {
      if (existing === null || prior?.status !== undefined) next.status = "active";
      const policy = intent.policy === undefined ? prior?.policy : intent.policy;
      if (policy !== undefined && policy !== null) next.policy = policy;
      if (intent.kind === "site") {
        next.targets = [{ uri: await checkSite(client, intent.siteRkey) }];
      } else {
        // Converting a canonical name into an alias could chain existing inbound aliases that
        // this narrow writer cannot enumerate. Refuse that transition rather than guessing.
        if (prior !== undefined && "targets" in prior) throw new RouteTargetError("cannot convert a target route into an alias; use a fresh alias name");
        const target = await client.getRecord(collection, intent.alias);
        if (target === null) throw new RouteTargetError("alias target does not exist");
        const targetValue = validateRouteRecord(target.value, collection);
        if (targetValue.status === "retired" || !("targets" in targetValue)) {
          throw new RouteTargetError("alias target must be an active route with targets; alias chains are forbidden");
        }
        next.alias = intent.alias;
      }
    }
    const record = validateRouteRecord(next, collection);
    // Compare locally encoded VALUES only for a no-op, never use these computed CIDs for CAS.
    if (existing !== null && await recordCid(existing.value) === await recordCid(record)) {
      return { routeUri, routeCid: existing.cid, name, collection, record, unchanged: true };
    }
    try {
      const written = await client.putRecord({ collection, rkey: name, value: record, swapRecord: expectedCid, swapCommit: head.cid });
      return {
        routeUri: written.uri, routeCid: written.cid, name, collection, record, unchanged: false,
        ...(existing !== null && intent.kind !== "retire" ? {
          warning: "Moving or reusing a name inherits its browser storage and permissions; the publisher is responsible for that continuity.",
        } : {}),
      };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }
}
